<script setup>
import {useAuthService} from "~/composables/useAuthService";
import {useUserFormPrompts} from "~/composables/useUserFormPrompts";
import {onMounted, onUnmounted, onErrorCaptured} from "vue";
import {useRoute, useRouter} from "vue-router";
import {showError} from "nuxt/app";
import AssistantPanel from "~/components/assistant/AssistantPanel.vue";
import PromptIntroDock from "~/components/prompts/PromptIntroDock.vue";
import MessagingDialog from "~/components/messaging/MessagingDialog.vue";
import CookieConsent from "~/components/layout/CookieConsent.vue";
import AppHandoff from "~/components/layout/AppHandoff.vue";

const {updateLocalUser, isAuthenticated, canUseAssistant, user, userRefreshed} = useAuthService();
const {promptWallDestination, promptWallShouldRefresh} = useUserFormPrompts();
const route = useRoute();
const router = useRouter();

const { addListener, removeLocalListenersWithEvent } = useListenerService();

/**
 * The required-prompt wall, applied once at the app's own mount.
 *
 * The wall itself is still the ROUTE (userFormPrompt.global.js), and this does
 * not replace it. What it adds is a check at the one moment the middleware
 * cannot cover: the route it decided about has already been decided, so a user
 * that only becomes blocked once the launch refresh lands is not held until the
 * NEXT navigation. On a landing page somebody is happy to sit on, that next
 * navigation may never come.
 *
 * It costs nothing in the ordinary case. The launch refresh (init) has already
 * run by the time this mounts, so `userRefreshed` is true and no request is
 * made; the check simply re-asks the question against the user in hand. The one
 * request it will spend is for the launch whose refresh did NOT land — which
 * updateLocalUser swallows deliberately, so nothing else would ever notice.
 *
 * app.vue mounts once per launch, not once per navigation, so this runs exactly
 * once. Exits answer "let them through" whatever the account says, so they are
 * skipped before the request rather than after it.
 */
const applyPromptWall = async () => {
  if (!isAuthenticated.value) return;
  if (promptWallShouldRefresh({
    authenticated: isAuthenticated.value,
    refreshed: userRefreshed.value,
    path: route.path,
  })) {
    await updateLocalUser();
  }
  const destination = promptWallDestination(route.fullPath, user.value?.pendingFormPrompt);
  if (destination) await router.replace(destination);
};

// A page that does `throw createError(...)` in async setup (e.g. a deleted tournament → 404)
// can escape Nuxt's error boundary during client-side back/forward navigation with the out-in
// page transition — the old page has already left and the error page never enters, leaving a
// blank screen that only a refresh fixes. Catch those status errors here and push them into
// Nuxt's reactive error state so error.vue renders reliably instead. error.vue is rendered
// above this component when an error is shown, so this never loops on itself.
onErrorCaptured((err) => {
  if (err && (err.statusCode || err.fatal)) {
    showError(err);
    return false; // handled — don't let it become an uncaught rejection / blank page
  }
});

onMounted(() => {
  addListener('api/v2/clubs:create', (data) => {
    updateLocalUser();
  });
  addListener('api/v2/clubs:delete', (data) => {
    updateLocalUser();
  });
  // Never allowed to break the app it is mounted in: a refused navigation (the
  // wall is already where it wants to go) or an unreachable API must leave the
  // page exactly as it was.
  applyPromptWall().catch(() => {});
});
onUnmounted(() => {
  removeLocalListenersWithEvent('api/v2/clubs:create');
  removeLocalListenersWithEvent('api/v2/clubs:delete');
});

</script>
<template>
  <div>
    <!-- Global Dialogs -->
    <messaging-dialog v-if="isAuthenticated" />
    <assistant-panel v-if="canUseAssistant" />
    <!-- An OPTIONAL set asks before it interrupts: a small docked card over a
         page that still works. A REQUIRED one is not drawn here — its blocking
         is the route redirect in userFormPrompt.global.js, which cannot be
         defeated by a component failing to mount. -->
    <prompt-intro-dock v-if="isAuthenticated" />
    <alert-display />
    <!-- PECR/GDPR cookie banner — analytics only load after an opt-in.
         The composable skips /embed pages entirely. -->
    <cookie-consent />
    <!-- Mobile web → app: fullscreen once on member-facing pages, snoozable
         7 days, never on invite/auth/admin/marketing (useMobileApp). -->
    <app-handoff />

    <!-- Layout System -->
    <NuxtLayout />
  </div>
</template>

<style>
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.2s;
}
.slide-left-enter-from {
  opacity: 0;
}
.slide-left-leave-to {
  opacity: 0;
}
.slide-right-enter-from {
  opacity: 0;
}
.slide-right-leave-to {
  opacity: 0;
}
</style>
